import type { PlannerProject, WardrobeModule } from "../types";

export type ProductFamily = "wardrobe" | "shelving" | "chest";
export type WorkflowPanel = "modules" | "shelving" | "chest" | "materials" | "internals" | "facades" | "room" | "summary";
export type Panel = "overview" | WorkflowPanel;

/**
 * Комод — отдельный раздел (замечание Сергея 21.09), но собирается тем же
 * движком, что стеллаж: это стеллаж, все ячейки которого — ящики.
 */
export const productFamilyFor = (module: Pick<WardrobeModule, "type" | "shelvingKind">): ProductFamily =>
  module.type === "shelving" ? module.shelvingKind === "chest" ? "chest" : "shelving" : "wardrobe";

export const PRODUCT_FAMILIES: readonly ProductFamily[] = ["wardrobe", "shelving", "chest"];

export const productFamilyTitles: Record<ProductFamily, string> = {
  wardrobe: "Шкафы", shelving: "Стеллажи", chest: "Комоды"
};

export const productFamilySingular: Record<ProductFamily, string> = {
  wardrobe: "шкаф", shelving: "стеллаж", chest: "комод"
};

/**
 * Новые заказы собираются вокруг одного изделия. Старые сохранения могли
 * содержать и шкафы, и стеллажи одновременно, поэтому здесь возвращается
 * массив: интерфейс сможет открыть такое сохранение без потери данных, но не
 * предложит смешивать категории в новом заказе.
 */
export function projectProductFamilies(project: PlannerProject): ProductFamily[] {
  const families = new Set(project.modules.map(productFamilyFor));
  return PRODUCT_FAMILIES.filter(family => families.has(family));
}

/**
 * Проект получает номер сам: «Новый проект» в переписке и в папке с PDF не
 * найти. Формат ГГММДД-NNNN — дата начала и четыре случайные цифры. Номер
 * заказа всё равно присвоит amoCRM, этот нужен до отправки.
 */
export const PROJECT_NUMBER_PATTERN = /^\d{6}-\d{4}$/;

export const createProjectNumber = (date = new Date(), random = Math.random) => {
  const part = (value: number) => String(value).padStart(2, "0");
  const tail = String(Math.floor(random() * 10_000) % 10_000).padStart(4, "0");
  return `${part(date.getFullYear() % 100)}${part(date.getMonth() + 1)}${part(date.getDate())}-${tail}`;
};

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

export const NEW_PROJECT_NAME = "Новый проект";

/**
 * Название проекта, пока клиент не дал своё (09.10, приёмка 1.1 и 1.3):
 * «Новый проект». Номер по порядку получает заявка на сервере, а не проект.
 */
export const autoProjectName = (_number: string, _family: ProductFamily | null) => NEW_PROJECT_NAME;

/** Прежние автоназвания: «Проект 260925-0482», «260925-0482 · Шкаф». */
const legacyAutoProjectName = (number: string, family: ProductFamily | null) =>
  family ? `${number} · ${capitalize(productFamilySingular[family])}` : `Проект ${number}`;

/**
 * Прежнее автоназвание с любым номером: «Проект 260930-9883», «260930-9883 ·
 * Шкаф». Встречается у проектов из кабинета и в сохранённых черновиках, где
 * номер проекта уже другой (приёмка 11.10, start-03).
 */
export const isLegacyAutoProjectName = (name: string) =>
  /^Проект \d{6}-\d{4}$/.test(name) || /^\d{6}-\d{4} · (Шкаф|Стеллаж|Комод)$/.test(name);

/** Название, которое проект получил сам и которое можно пересчитать. */
export const isAutoProjectName = (name: string, number: string) =>
  !name ||
  name === NEW_PROJECT_NAME ||
  isLegacyAutoProjectName(name) ||
  name === legacyAutoProjectName(number, null) ||
  (Object.keys(productFamilySingular) as ProductFamily[]).some(
    family => name === legacyAutoProjectName(number, family)
  );

export function projectAcceptsFamily(project: PlannerProject, family: ProductFamily) {
  const families = projectProductFamilies(project);
  return families.length === 0 || (families.length === 1 && families[0] === family);
}

/**
 * Комната — первый шаг (замечание Сергея 21.09): высота потолка задаёт
 * высоту шкафа и антресоль, поэтому её настраивают до размеров изделия.
 */
export const productWorkflows: Record<ProductFamily, WorkflowPanel[]> = {
  wardrobe: ["room", "modules", "materials", "internals", "facades", "summary"],
  // Ячейки намеренно вне линейки: это «Продвинутый конструктор», отдельный экран.
  shelving: ["room", "shelving", "materials", "facades", "summary"],
  // У комода ячейки всегда ящики: размеры, колонки и число ящиков — на одном экране.
  chest: ["room", "chest", "materials", "facades", "summary"]
};

/** Главный шаг изделия — где задаются его размеры. */
export const productMainPanel = (family: ProductFamily): WorkflowPanel =>
  family === "shelving" ? "shelving" : family === "chest" ? "chest" : "modules";

/** Какой шаг открыть при выборе изделия: новый заказ начинается с комнаты. */
export const productEntryPanel = (project: PlannerProject, family: ProductFamily): WorkflowPanel =>
  productModules(project, family).length > 0 ? productMainPanel(family) : productWorkflows[family][0]!;

export function resolveProductPanel(panel: Panel, family: ProductFamily | null): Panel {
  if (panel === "overview" || panel === "room" || panel === "summary") return panel;
  if (!family) return "overview";
  if (panel === "modules" || panel === "shelving" || panel === "chest") return productMainPanel(family);
  if (family === "chest" && panel === "internals") return "chest";
  return panel;
}

export function productModules(project: PlannerProject, family: ProductFamily) {
  return project.modules.filter(module => productFamilyFor(module) === family);
}

export function productSelection(project: PlannerProject, family: ProductFamily): WardrobeModule | undefined {
  const modules = productModules(project, family);
  return modules.find(module => module.id === project.selectedModuleId) ?? modules[0];
}

export function canEditProduct(module: Pick<WardrobeModule, "type" | "shelvingKind">, family: ProductFamily | null) {
  return family !== null && productFamilyFor(module) === family;
}

export function workflowTitle(panel: WorkflowPanel, family: ProductFamily | null) {
  if (panel === "internals" && family === "shelving") return "Ячейки и перегородки";
  if (panel === "shelving") return "Размеры и сетка";
  if (panel === "facades" && family === "shelving") return "Фасады ячеек";
  if (panel === "facades" && family === "chest") return "Фасады и ручки";
  return {
    modules: "Корпусы", shelving: "Размеры и сетка", chest: "Размеры и ящики", materials: "Цвет и материал",
    internals: "Наполнение", facades: "Фасады и ручки", room: "Комната", summary: "Итог проекта"
  }[panel];
}

export function workflowShortTitle(panel: WorkflowPanel, family: ProductFamily | null) {
  if (panel === "internals" && family === "shelving") return "Ячейки";
  return { modules: "Корпус", shelving: "Размеры", chest: "Размеры", materials: "Материал", internals: "Наполнение",
    facades: "Фасады", room: "Комната", summary: "Итог" }[panel];
}
